import {
  Address,
  AddressInput,
  Company,
  CompanySummary,
  CompanyTypes,
  CreateDirectoryCompanyInput,
  EmailAddressInput,
  LicenseInput,
  PartyCompany,
  PartyCompanyInput,
  PhoneNumberInput,
} from '../__graphql_generated__';

const safeArray = <T>(input: T[] | undefined | null): T[] => input ?? [];
export const toCompanyInputsV2 = () => {
  const toCompanyTypes = (company: Company | PartyCompany | CompanySummary): string[] => {
    if (!company?.companyTypes) {
      return [];
    }
    return safeArray(company?.companyTypes)
      .flatMap((type: CompanyTypes | string | null) => {
        if (typeof type === 'string') {
          return type;
        }

        if (typeof type === 'object' && type !== null && 'types' in type && Array.isArray(type.types)) {
          return type.types.map((t) => t?.id);
        }

        return [];
      })
      .filter((id): id is string => id !== undefined); // Filter out undefined values
  };

  const toEmailAddressInput = (company: Company | PartyCompany | CompanySummary): EmailAddressInput[] => {
    if ('primaryEmail' in company) {
      return [
        {
          emailAddress: company?.primaryEmail ?? '',
          isPrimary: true,
        },
      ];
    }
    if ('emailAddresses' in company) {
      return safeArray(company.emailAddresses).map((email) => ({
        emailAddress: email?.emailAddress ?? '',
        isPrimary: email?.isPrimary ?? false,
      }));
    }
    return [];
  };

  const toPhoneNumberInput = (company: Company | PartyCompany | CompanySummary): PhoneNumberInput[] => {
    if ('phoneNumbers' in company && Array.isArray(company?.phoneNumbers)) {
      const phoneNumbers = company.phoneNumbers;
      if (phoneNumbers.length === 0) {
        return [];
      } else {
        return phoneNumbers.map((phone) => ({
          type: phone?.type ?? '', // Ensure the type property is included
          phoneNumber: phone?.phoneNumber ?? '',
          ext: phone?.ext ?? undefined,
          isPrimary: phone?.isPrimary ?? false,
        }));
      }
    }
    const phoneNumberInput: PhoneNumberInput[] | undefined = [];
    if ('primaryPhone' in company) {
      const primaryPhone = createPhoneNumberByType('primaryPhone', company);
      primaryPhone ? phoneNumberInput.push(primaryPhone) : null;
    }

    return phoneNumberInput ?? [];
  };

  const toLicenseInput = (company: Company | PartyCompany | CompanySummary): LicenseInput[] => {
    if ('licenses' in company && Array.isArray(company?.licenses)) {
      const licenses = company?.licenses;
      if (licenses.length === 0) {
        return [];
      } else {
        return licenses.map((license) => ({
          licensedState: license?.licensedState,
          licenseNumber: license?.licenseNumber,
        }));
      }
    }
    return [];
  };

  const toAddressInput = (company: Company | PartyCompany | CompanySummary): AddressInput[] => {
    if ('addresses' in company && Array.isArray(company?.addresses)) {
      const addresses = company.addresses;
      if (addresses.length === 0) {
        return [];
      } else {
        return addresses.map((address) => ({
          addressType: address?.addressType ?? undefined,
          address1: address?.address1 ?? undefined,
          address2: address?.address2 ?? undefined,
          city: address?.city ?? undefined,
          countyName: address?.countyName ?? address?.county ?? undefined,
          country: address?.country ?? undefined,
          countyFips: address?.countyFips ?? undefined,
          postalCode: address?.postalCode ?? undefined,
          stateOrProvince: address?.stateOrProvince ?? undefined,
          usePrimaryAddress: address?.usePrimaryAddress ?? undefined,
        }));
      }
    }

    const addressInput: AddressInput[] | undefined = [];

    if ('mailingAddress' in company) {
      const mailingAddress = createAddressByType('mailingAddress', company);
      mailingAddress ? addressInput.push(mailingAddress) : null;
    }
    if ('primaryAddress' in company) {
      const primaryAddress = createAddressByType('primaryAddress', company);
      primaryAddress ? addressInput.push(primaryAddress) : null;
    }
    return addressInput ?? [];
  };

  const toPartyCompany = (
    company: Company | PartyCompany | CompanySummary,
    directoryCompanyId?: string,
  ): PartyCompanyInput => {
    const directoryCompanyReferenceId = () => {
      if ('directoryCompanyReferenceId' in company) {
        return company?.directoryCompanyReferenceId;
      }
      if (directoryCompanyId) {
        return directoryCompanyId;
      }
      if ('id' in company) {
        return company?.id;
      }
      return undefined;
    };

    return {
      directoryCompanyReferenceId: directoryCompanyReferenceId(),
      companyName: company?.companyName,
      companyTypes: toCompanyTypes(company),
      alternateSearchName: company?.alternateSearchName,
      emailAddresses: toEmailAddressInput(company),
      phoneNumbers: toPhoneNumberInput(company),
      addresses: toAddressInput(company),
      website: company?.website,
      altaId: 'altaId' in company ? company?.altaId : undefined,
      lenderLanguage: 'lenderLanguage' in company ? company?.lenderLanguage : undefined,
      lenderLanguageDefaultType:
        'lenderLanguageDefaultType' in company ? company?.lenderLanguageDefaultType : undefined,
      lenderLanguageOptions: 'lenderLanguageOptions' in company ? company?.lenderLanguageOptions : undefined,
      licenses: toLicenseInput(company),
      nmlsId: 'nmlsId' in company ? company?.nmlsId : undefined,
      taxId: 'taxId' in company ? company?.taxId : undefined,
      taxIdType: 'taxIdType' in company ? company?.taxIdType : undefined,
      tenantOrgReferenceId: 'tenantOrgReferenceId' in company ? company?.tenantOrgReferenceId : undefined,
      wireInstructions: 'wireInstructions' in company ? company?.wireInstructions : undefined,
    };
  };

  const toDirectoryCompany = (company: Company | PartyCompany): CreateDirectoryCompanyInput => {
    const returnData: CreateDirectoryCompanyInput = {
      companyName: company?.companyName,
      alternateSearchName: company?.alternateSearchName ?? undefined,
      taxIdType: company?.taxIdType ?? undefined,
      taxId: company?.taxId ?? undefined,
      altaId: company?.altaId ?? undefined,
      nmlsId: company?.nmlsId ?? undefined,
      website: company?.website ?? undefined,
      wireInstructions: company?.wireInstructions,
      emailAddresses: toEmailAddressInput(company),
      phoneNumbers: toPhoneNumberInput(company),
      companyTypes: toCompanyTypes(company),
      licenses: toLicenseInput(company),
      addresses: toAddressInput(company),
    };

    // Ensure addresses are included even if empty
    if (!returnData.addresses) {
      returnData.addresses = [];
    }

    // Narrow the type to PartyCompany if 'orgIds' exists
    if ('orgIds' in company) {
      returnData.orgIds = company.orgIds;
    }

    return returnData;
  };

  return {
    toPartyCompany,
    toDirectoryCompany,
    getCompanyTypes: toCompanyTypes,
    getCompanyEmail: toEmailAddressInput,
    getCompanyPhoneNumbers: toPhoneNumberInput,
    getCompanyLicense: toLicenseInput,
    getCompanyAddresses: toAddressInput,
  };
};

const createPhoneNumberByType = (type: string, company: CompanySummary): PhoneNumberInput | undefined => {
  const phoneNumber = company[type as keyof CompanySummary] as PhoneNumberInput | undefined;
  return {
    type: phoneNumber?.phoneNumber.type ?? '',
    phoneNumber: phoneNumber?.phoneNumber ?? undefined,
    ext: phoneNumber?.ext ?? undefined,
    isPrimary: type === 'primaryPhone',
  };
};

const createAddressByType = (type: string, company: CompanySummary): AddressInput | undefined => {
  const address = company[type as keyof CompanySummary] as Address | undefined;
  return {
    addressType: type,
    address1: address?.address1 ?? undefined,
    address2: address?.address2 ?? undefined,
    city: address?.city ?? undefined,
    countyName: address?.countyName ?? address?.county ?? undefined,
    country: address?.country ?? undefined,
    countyFips: address?.countyFips ?? undefined,
    postalCode: address?.postalCode ?? undefined,
    stateOrProvince: address?.stateOrProvince ?? undefined,
    usePrimaryAddress: type === 'primaryAddress',
  };
};
